import { useEffect, useState } from "react";
import styled from "styled-components";
import IconButton from "./IconButton";
import {
  HiOutlineSpeakerWave,
  HiOutlineSpeakerXMark,
  HiOutlineSquare2Stack,
} from "react-icons/hi2";
import toast from "react-hot-toast";

const Actions = styled.div`
  display: flex;
  gap: 0.8rem;

  & button {
    padding: 0.2rem;
    opacity: 0.6;

    display: flex;
    align-items: center;
    gap: 0.8rem;

    &:hover {
      padding-left: 0.4rem;
      padding-right: 0.4rem;

      background-color: var(--color-primary);
      opacity: 1;
      color: var(--color-text);
    }

    &:hover span {
      display: inline;
    }

    &:hover svg {
      color: var(--color-text);
    }
  }

  & button span {
    display: none;
  }
`;

function detectLang(text) {
  // Heurística: caracteres acentuados específicos de português indicam PT-BR.
  // Mensagens em inglês raramente contêm esses chars; mensagens em PT-BR
  // praticamente sempre têm pelo menos um.
  if (/[àáâãéêíóôõúç]/i.test(text)) {
    return "pt-BR";
  }
  return "en-US";
}

function getVoiceForLang(lang) {
  const voices = window.speechSynthesis.getVoices();
  // Tenta match exato (ex: "pt-BR")
  let voice = voices.find((v) => v.lang === lang);
  if (voice) return voice;
  // Fallback pra família de idioma (ex: qualquer voz "pt-*")
  const family = lang.split("-")[0];
  voice = voices.find((v) => v.lang.startsWith(family));
  return voice;
}

/**
 * Renders two buttons for an answer:
 * - "Listen" / "Stop" toggle that reads the answer aloud via the browser's
 *   SpeechSynthesis API. Clicking again while speech is active cancels it.
 *   The state resets automatically when the last sentence finishes or errors.
 * - "Copy" copies the answer to the clipboard.
 *
 * As duas ações levam textos DIFERENTES, de propósito:
 *
 * `speakText` é só o CORPO. A divulgação do Art. 50 não é falada porque quem
 * ouve está olhando a mesma tela, onde o aviso permanente do `DialogInput` já
 * está visível — falar "Atcheza is AI and can make mistakes…" depois de cada
 * resposta é ruído, não informação. A citação também fica de fora: ler
 * "[fontes: Portaria IBAMA 171/1998; Manhães 2003]" em voz alta é ler
 * pontuação, e a fonte segue à vista na caixa de referência ao lado. O
 * contra-argumento considerado foi acessibilidade — mas o aviso permanente é
 * texto no DOM, então leitor de tela o anuncia; este botão é conveniência de
 * TTS pro corpo da resposta, não substituto de leitor de tela.
 *
 * `copyText` leva TUDO, incluindo o rodapé. O texto copiado sai da página e
 * deixa o aviso permanente atrás: num documento ou e-mail não há aviso fixo
 * nenhum, então a linha viaja com o texto. Ver o comentário em Answer.jsx.
 *
 * @param {string} copyText - Corpo + citação + rodapé, pro clipboard.
 * @param {string} speakText - Só o corpo, pra síntese de voz.
 * @returns {JSX.Element}
 */
function AnswerActions({ copyText, speakText }) {
  const speechAvailable =
    typeof window !== "undefined" && "speechSynthesis" in window;
  const [isSpeaking, setIsSpeaking] = useState(false);

  // Cancel any in-flight speech if the component unmounts while speaking.
  useEffect(() => {
    return () => {
      if (speechAvailable) {
        window.speechSynthesis.cancel();
      }
    };
  }, [speechAvailable]);

  function toggleSpeech() {
    if (!speechAvailable) return;

    // If currently speaking, stop immediately.
    if (isSpeaking) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }

    // Cancel any stale queued utterances before starting fresh.
    window.speechSynthesis.cancel();

    const sentences = speakText
      .split(".")
      .map((sentence) => sentence.trim())
      .filter(Boolean);

    if (sentences.length === 0) return;

    setIsSpeaking(true);

    const lang = detectLang(speakText);
    const voice = getVoiceForLang(lang);

    sentences.forEach((sentence, idx) => {
      const utterance = new SpeechSynthesisUtterance();
      utterance.text = sentence;
      utterance.lang = lang;
      if (voice) utterance.voice = voice;
      utterance.volume = 0.8;
      utterance.rate = 1;

      // Reset state when the last sentence finishes or errors.
      if (idx === sentences.length - 1) {
        utterance.onend = () => setIsSpeaking(false);
        utterance.onerror = () => setIsSpeaking(false);
      }

      window.speechSynthesis.speak(utterance);
    });
  }

  function copyMessage() {
    navigator.clipboard.writeText(copyText).then(() => {
      toast.success("Copied to clipboard successfully.");
    });
  }

  return (
    <Actions className="actions">
      {speechAvailable && (
        <IconButton variant="secondary" onClick={toggleSpeech}>
          {isSpeaking ? <HiOutlineSpeakerXMark /> : <HiOutlineSpeakerWave />}
          <span>{isSpeaking ? "Stop" : "Listen"}</span>
        </IconButton>
      )}

      <IconButton variant="secondary" onClick={copyMessage}>
        <HiOutlineSquare2Stack />
        <span>Copy</span>
      </IconButton>
    </Actions>
  );
}

export default AnswerActions;
